import posthog from 'posthog-js'
import { useStorage } from '@vueuse/core'
import { IS_POSTHOG_ENABLED } from '@/constants/analytics'

import { MERCHANT_IDS, type MerchantKey } from '@/constants/merchants'

import type { Store } from '@/views/contests/Components/types/global'
import type { GlobalConfig } from '../types/types'

const defaultUserObject = {
  configs: [
    {
      id: 0,
      key: '',
      value: ''
    }
  ],
  id: null,
  publicId: '',
  name: '',
  firstname: '',
  lastname: '',
  email: '',
  jobTitle: '',
  hasToChangePassword: false,
  phone: '',
  passwordSend: false,
  lastLoginDate: '',
  createdAt: '',
  formattedPhone: '',
  isPhoneConfirmed: false,
  countryId: 0,
  language: {
    iso639_1: 'fr',
    code: ''
  },
  languageId: 21,
  role_template_id: 0,
  roleTemplate: {
    id: 0,
    slug: ''
  },
  merchant: {
    activityType: '',
    businessCrmIdentifier: '',
    id: 0,
    publicId: '',
    name: '',
    slug: '',
    smsName: '',
    employeeTabletCode: '',
    siouplaitId: '',
    isSelfServe: false,
    webAppUrl: '',
    webappLink: '',
    fallbackLanguageId: 0,
    createdAt: '',
    merchantActivityId: null,
    media: {
      logo: '',
      coverA: '',
      coverB: '',
      coverC: '',
      coverD: ''
    }
  },
  selectedStore: 0
}
const displaySmsPackUpsellBanner = useStorage('displaySmsPackUpsellBanner', true)
const user = useStorage('user', defaultUserObject)
const userHasClients = useStorage<boolean | null>('userHasClients', null)
const jwt = useStorage('jwt', '')
const merchantName = useStorage('merchantName', '')
const merchantLogo = useStorage('merchantLogo', '')
const setupToken = useStorage('setupToken', '')
const onboardingDate = useStorage('onboardingDate', '')
const dismissAnalyticsBlock = useStorage('dismissAnalyticsBlock', false)
const isSupportConnection = useStorage('isSupportConnection', false)
const rememberMe = useStorage('rememberMe', false)
const hasWallet = useStorage('hasWallet', false)
const hasAllPartnersWithSegmentationSupport = useStorage('hasAllPartnersWithSegmentationSupport', false)
const stores = useStorage<Store[]>('stores', [])
const customerTags = useStorage('customerTags', [])
const frequencyTypes = useStorage('frequencyTypes', [])
const frequencyTypesSegments = useStorage('frequencyTypesSegments', [])
const displayBusinessDashboard = useStorage('displayBusinessDashboard', false)
const permissions = useStorage('permissions', {})
const permissionsByStores = useStorage('permissionsByStores', {})
const subscriptions = useStorage<Record<string, boolean>>('subscriptions', {})
const sms = useStorage('sms', {
  price: 0,
  countryId: null
})
const languages = useStorage('languages', [])
const selectedStore = useStorage('selectedStore', 0)
const selectedStoreFilter = useStorage<number[]>('selectedStoreFilter', [])
const mandates = useStorage('mandates', [])
const hasCheckedLoyaltyModal = useStorage('hasCheckedLoyaltyModal', false)
const globalConfig = useStorage<GlobalConfig>('globalConfig', {
  merchant: {},
  user: {},
  store: {}
})
const addonsEventsRecorder = useStorage('addonsEventsRecorder', [])
const moduleAccesses = useStorage<Record<string, boolean>>('moduleAccesses', {})
const moduleAccessesByStores = useStorage<Record<string, string[]>>('moduleAccessesByStores', {})

export default function useLocalStorage() {
  const resetUseStorageItems = () => {
    permissions.value = {}
    permissionsByStores.value = {}
    subscriptions.value = {}
    user.value = defaultUserObject
    userHasClients.value = null
    jwt.value = ''
    stores.value = []
    customerTags.value = []
    frequencyTypes.value = []
    frequencyTypesSegments.value = []
    displayBusinessDashboard.value = false
    merchantName.value = ''
    merchantLogo.value = ''
    setupToken.value = ''
    onboardingDate.value = ''
    dismissAnalyticsBlock.value = false
    isSupportConnection.value = false
    hasWallet.value = false
    hasAllPartnersWithSegmentationSupport.value = false
    rememberMe.value = false
    languages.value = []
    sms.value = {
      price: 0,
      countryId: null
    }
    displaySmsPackUpsellBanner.value = true
    mandates.value = []
    selectedStore.value = 0
    selectedStoreFilter.value = []
    hasCheckedLoyaltyModal.value = false
    globalConfig.value = {
      merchant: {},
      user: {},
      store: {}
    }
    moduleAccesses.value = {}
    moduleAccessesByStores.value = {}

    if (IS_POSTHOG_ENABLED) {
      posthog.reset()
    }
  }

  const isMerchant = (key: MerchantKey) => user.value.merchant.id === MERCHANT_IDS[key]

  return {
    addonsEventsRecorder,
    customerTags,
    dismissAnalyticsBlock,
    displayBusinessDashboard,
    displaySmsPackUpsellBanner,
    frequencyTypes,
    frequencyTypesSegments,
    globalConfig,
    hasAllPartnersWithSegmentationSupport,
    hasCheckedLoyaltyModal,
    hasWallet,
    isMerchant,
    isSupportConnection,
    jwt,
    languages,
    mandates,
    merchantLogo,
    merchantName,
    moduleAccesses,
    moduleAccessesByStores,
    onboardingDate,
    permissions,
    permissionsByStores,
    rememberMe,
    resetUseStorageItems,
    selectedStore,
    selectedStoreFilter,
    setupToken,
    sms,
    stores,
    subscriptions,
    user,
    userHasClients
  }
}
